/** TravelMenu — GameModule-Orchestrator. Delegiert an HexPicker, WizardSync, TravelSync, Events. */
import type { SupabaseClient } from '@supabase/supabase-js';
import type { GameModule } from '../core/GameModule';
import type { Engine } from '../core/Engine';
import type { TravelConfig } from '../config/TravelConfig';
import type { WorldInfo } from '../config/WorldConfig';
import { createTravelMenuDOM, populatePlanetSelect, setLascheActive, updateStepIndicator } from './TravelMenuDOM';
import type { TravelMenuDOMResult, FleetTabId } from './TravelMenuDOM';
import { TravelMenuState } from './TravelMenuState';
import type { TravelLocation, ActiveTravel } from './TravelMenuState';
import { createSelectionBanner, createConfirmDialog, removeOverlayElement } from './TravelMenuHexPicker';
import { syncStep1, syncSliders, syncWarpInfo, syncModeCards, syncSendBtn } from './TravelMenuWizardSync';
import { syncActiveTravels, syncInactiveTravels } from './TravelMenuTravelSync';
import { bindTravelMenuEvents } from './TravelMenuEvents';

const SLIDE_MS = 250;

export class TravelMenu implements GameModule {
  readonly name = 'TravelMenu';
  private _engine!: Engine;
  private _state!: TravelMenuState;
  private _dom: TravelMenuDOMResult | null = null;
  private _visible = false;
  private _activeTab: FleetTabId = 'hub';
  private _currentStep: 1 | 2 | 3 = 1;
  private _barEls = new Map<string, HTMLDivElement>();
  private _inactiveEls = new Map<string, HTMLDivElement>();
  private _tickAcc = 0;
  private _pollAcc = 0;
  private _banner: HTMLDivElement | null = null;
  private _dialog: HTMLDivElement | null = null;
  private _anim: Animation | null = null;
  private _refetching = false;
  private _travelConfig: TravelConfig;
  private _worldInfo: WorldInfo;
  private _sunKeysProvider: (() => ReadonlySet<number>) | null;
  constructor(
    private _sb: SupabaseClient, private _worldId: string, private _playerId: string,
    travelConfig: TravelConfig, worldInfo: WorldInfo,
    sunKeysProvider?: (() => ReadonlySet<number>) | null,
  ) {
    this._travelConfig = travelConfig;
    this._worldInfo = worldInfo;
    this._sunKeysProvider = sunKeysProvider ?? null;
  }

  init(engine: Engine): void {
    this._engine = engine;
    this._state = new TravelMenuState(this._sb, this._worldId, this._playerId);
    engine.events.on<null>('travel:open-menu', () => this._open());
    engine.events.on<null>('travel:close-menu', () => this._close());
    engine.events.on<{ q: number; r: number }>('travel:hex-clicked', (d) => {
      if (this._state.selectingOnMap) this._onHexClicked(d.q, d.r);
    });
    this._state.loadActiveTravels().then(() => this._emitFleetData());
  }

  update(dt: number): void {
    this._tickAcc += dt;
    if (this._tickAcc < 0.5) return;
    this._tickAcc = 0;
    // Periodic re-fetch (every 10s) for enemy fleet visibility + own fleet arrivals
    this._pollAcc += 0.5;
    if (this._pollAcc >= 10 && !this._refetching) {
      this._pollAcc = 0;
      this._refetching = true;
      this._state.loadActiveTravels().then(() => {
        this._refetching = false;
        if (this._dom) this._syncLists();
        this._emitFleetData();
      });
    }
    // Immediate re-fetch when own fleet arrives
    if (this._state.updateProgress() && !this._refetching) {
      this._refetching = true;
      this._state.loadActiveTravels().then(() => {
        this._refetching = false;
        if (this._dom) this._syncLists();
        this._emitFleetData();
      });
    }
    if (!this._visible || !this._dom) { this._emitFleetData(); return; }
    this._syncLists();
    this._emitFleetData();
  }

  dispose(): void { this._close(); }

  private async _open(): Promise<void> {
    if (this._visible) return;
    this._visible = true;
    if (!this._dom) {
      this._dom = createTravelMenuDOM(() => this._close());
      bindTravelMenuEvents(this._dom, this._state, {
        switchTab: (id) => this._switchTab(id), goToStep: (s) => this._goToStep(s),
        canAdvance: () => this._canAdvance(), currentStep: () => this._currentStep,
        updateNextBtn: () => this._updateNextBtn(),
        minimizeOverlay: (side) => { this._state.selectingOnMap = side; this._minimizeOverlay(); },
        fullSync: () => this._fullSync(),
        getPathfindingContext: () => {
          const sunKeys: number[] = [];
          if (this._sunKeysProvider) for (const k of this._sunKeysProvider()) sunKeys.push(k);
          return { travelConfig: this._travelConfig, worldInfo: this._worldInfo, sunKeys };
        },
      });
      document.body.appendChild(this._dom.root);
    }
    this._dom.root.style.display = 'flex';
    this._animatePanel(true);
    this._switchTab(this._activeTab);
    this._goToStep(1);
    this._dom.statusEl.textContent = 'Lade...';
    await Promise.all([this._state.loadOwnPlanets(), this._state.loadActiveTravels()]);
    this._dom.statusEl.textContent = '';
    this._fullSync();
  }

  private _close(): void {
    if (!this._visible || !this._dom) return;
    this._visible = false;
    this._state.selectingOnMap = null;
    this._banner = removeOverlayElement(this._banner);
    this._dialog = removeOverlayElement(this._dialog);
    this._engine.events.emit('hex-picker:set-enabled', { enabled: false });
    this._animatePanel(false);
    this._engine.events.emit('travel:menu-closed', null);
  }

  private _animatePanel(opening: boolean): void {
    this._anim?.cancel();
    const m = window.innerWidth < 768;
    const prop = m ? 'transform' : 'opacity';
    const from = m ? (opening ? 'translateX(100%)' : 'translateX(0)') : (opening ? '0' : '1');
    const to = m ? (opening ? 'translateX(0)' : 'translateX(100%)') : (opening ? '1' : '0');
    if (opening && !m) this._dom!.root.style.opacity = '0';
    this._anim = this._dom!.root.animate([{ [prop]: from }, { [prop]: to }], {
      duration: opening ? (m ? SLIDE_MS : 200) : (m ? SLIDE_MS * 0.7 : 150),
      easing: opening ? 'cubic-bezier(0.22,0.85,0.32,1)' : 'ease-in', fill: 'forwards',
    });
    if (!opening) this._anim.onfinish = () => { if (this._dom && !this._visible) this._dom.root.style.display = 'none'; this._anim = null; };
  }

  private _switchTab(id: FleetTabId): void {
    if (!this._dom) return;
    this._activeTab = id;
    setLascheActive(this._dom.tabBtns.active, id === 'active');
    setLascheActive(this._dom.tabBtns.inactive, id === 'inactive');
    setLascheActive(this._dom.tabBtns.hub, id === 'hub');
    for (const [tab, el] of [['active', this._dom.activeContent], ['inactive', this._dom.inactiveContent], ['hub', this._dom.hubContent]] as const)
      el.style.display = tab === id ? 'flex' : 'none';
  }

  private _goToStep(step: 1 | 2 | 3): void {
    if (!this._dom) return;
    this._currentStep = step;
    this._dom.step1El.style.display = step === 1 ? 'flex' : 'none';
    this._dom.step2El.style.display = step === 2 ? 'flex' : 'none';
    this._dom.step3El.style.display = step === 3 ? 'flex' : 'none';
    updateStepIndicator(this._dom.stepIndicator, step);
    this._dom.backBtn.style.visibility = step === 1 ? 'hidden' : 'visible';
    this._dom.nextBtn.style.display = step === 3 ? 'none' : 'block';
    this._syncCurrentStep(); this._updateNextBtn();
  }

  private _canAdvance(): boolean {
    if (this._currentStep === 1) return this._state.stationedSourceId ? !!this._state.selectedEnd : !!this._state.selectedStart && !!this._state.selectedEnd;
    return this._currentStep === 2 ? this._state.totalSelectedShips > 0 : true;
  }

  private _updateNextBtn(): void {
    if (!this._dom) return;
    const c = this._canAdvance();
    Object.assign(this._dom.nextBtn.style, { opacity: c ? '1' : '0.35', pointerEvents: c ? 'auto' : 'none' });
  }

  private _onHexClicked(q: number, r: number): void {
    this._dialog = removeOverlayElement(this._dialog);
    this._dialog = createConfirmDialog(q, r, (cq, cr) => this._confirmHex(cq, cr), () => { this._dialog = removeOverlayElement(this._dialog); });
  }

  private _confirmHex(q: number, r: number): void {
    const loc: TravelLocation = { q, r, label: `Hex (${q},${r})`, isPlanet: false, isSun: false, planetId: null, sunHexKey: null };
    if (this._state.selectingOnMap === 'start') { this._state.selectedStart = loc; this._state.shipSliders = [0, 0, 0, 0, 0, 0, 0, 0]; this._state.stationedSourceId = null; }
    else { this._state.selectedEnd = loc; }
    this._state.selectingOnMap = null; this._restoreOverlay();
    if (this._dom) { syncStep1(this._dom, this._state); this._updateNextBtn(); }
  }

  private _minimizeOverlay(): void {
    if (!this._dom) return;
    Object.assign(this._dom.root.style, { opacity: '0.12', pointerEvents: 'none' });
    this._engine.events.emit('hex-picker:set-enabled', { enabled: true });
    this._banner = createSelectionBanner(this._state.selectingOnMap === 'start', () => { this._state.selectingOnMap = null; this._restoreOverlay(); });
  }

  private _restoreOverlay(): void {
    if (!this._dom) return;
    Object.assign(this._dom.root.style, { opacity: '1', pointerEvents: 'auto' });
    this._engine.events.emit('hex-picker:set-enabled', { enabled: false });
    this._banner = removeOverlayElement(this._banner); this._dialog = removeOverlayElement(this._dialog);
  }

  private _fullSync(): void {
    if (!this._dom) return;
    populatePlanetSelect(this._dom.startSelect, this._state.ownPlanets, 'Startplanet w\u00E4hlen...');
    populatePlanetSelect(this._dom.endSelect, this._state.ownPlanets, 'Zielplanet w\u00E4hlen...');
    this._syncCurrentStep(); this._syncLists(); this._updateNextBtn(); syncSendBtn(this._dom, this._state); this._emitFleetData();
  }

  private _syncCurrentStep(): void {
    const d = this._dom, s = this._state; if (!d) return;
    if (this._currentStep === 1) syncStep1(d, s);
    else if (this._currentStep === 2) { syncSliders(d, s); syncWarpInfo(d, s); }
    else { syncModeCards(d, s); syncSendBtn(d, s); }
  }

  private _syncLists(): void {
    if (!this._dom) return;
    syncActiveTravels(this._dom.activeTravelsContainer, this._state, this._barEls);
    syncInactiveTravels(this._dom.inactiveTravelsContainer, this._state, this._inactiveEls, (f) => this._startSendFromHex(f));
  }

  private _startSendFromHex(f: ActiveTravel): void {
    const s = this._state;
    s.selectedStart = { q: f.end.q, r: f.end.r, label: `Hex (${f.end.q},${f.end.r})`, isPlanet: false, isSun: false, planetId: null, sunHexKey: null };
    s.selectedEnd = null; for (let i = 0; i < 8; i++) s.shipSliders[i] = f.ships[i];
    s.stationedSourceId = f.id; this._switchTab('hub'); this._goToStep(1);
  }

  private _emitFleetData(): void {
    this._engine.events.emit('travel:fleets-updated', {
      traveling: this._state.travelingTravels,
      stationed: this._state.stationedTravels,
      otherTraveling: this._state.otherTravelingTravels,
      otherStationed: this._state.otherStationedTravels,
      otherPlanetShips: this._state.otherPlanetShips,
    });
  }
}
